/**
 * 录入适配器的共同形状。
 *
 * 三种录入（手工 / 拍照 / 语音）在前端也统一成同一个接口。
 * 页面只遍历适配器注册表渲染入口，不写任何 if (source === 'voice') 这类分支。
 * 加第四种入口（条码、微信转发）= 新增一个文件 + 注册一行。
 */

import type { IntakeDraft, IntakeSource } from '@fridge/shared';

import type { IntakeCapabilities } from '../api/client';

/** 该适配器依赖哪种 AI 能力。none 表示零依赖，永远可用。 */
export type IntakeRequirement = 'none' | 'text' | 'vision';

export interface IntakeAdapter {
  source: IntakeSource;
  label: string;
  hint: string;
  requires: IntakeRequirement;
  /** 图标用内联 SVG path，避免引图标库 */
  iconPath: string;
}

export function isAdapterAvailable(
  adapter: IntakeAdapter,
  caps: IntakeCapabilities | null,
): boolean {
  if (adapter.requires === 'none') return true;
  if (!caps) return false;
  return adapter.requires === 'text' ? caps.textParse : caps.vision;
}

/** 能力未就绪时给用户的解释 */
export function unavailableReason(adapter: IntakeAdapter): string {
  return adapter.requires === 'vision'
    ? '服务端未配置视觉模型，拍照识别暂时用不了，先用文字录吧。'
    : '服务端未配置文本模型，这句先按文字规则粗拆，请仔细核对。';
}

/**
 * 兜底草稿：任何 AI 链路失败时，都要能沉到手工录入。
 * 把手里的线索（原文/转录文本）一并塞进去，用户不用重头打字。
 */
export function toManualFallbackDraft(
  from: IntakeSource,
  transcript: string | null,
  reason: string,
  partialItems: IntakeDraft['items'] = [],
): IntakeDraft {
  return {
    clientDraftId: `id_local_${Date.now().toString(36)}`,
    source: 'manual',
    items: partialItems,
    warnings: [`${reason}已切换为手工录入，${transcript ? '原文已保留在下方。' : '直接添加食材即可。'}`],
    transcript,
    fallbackFrom: from,
    createdAt: new Date().toISOString(),
  };
}
